'use client';

import { Wallet } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { creditForecast } from '@/lib/credit-forecast';
import { cn } from '@/lib/utils';
import { useCreditBalance } from '@/hooks/use-credit-balance';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { LocaleLink } from '@/components/locale-link';

/**
 * Header credit balance pill — ambient credit awareness on every page.
 *
 * Colour states: normal, amber below 20% of the period allowance, red at 0.
 * Click opens a small panel with balance / allowance / purchased plus
 * "Top up" and "Usage" links. Renders nothing for guests or while the first
 * snapshot loads (no layout jank: the header already flexes).
 *
 * `compact` drops the label to number-only for the mobile header row.
 */
export function CreditsPill({ compact = false }: { compact?: boolean }) {
  const { t, i18n } = useTranslation();
  const { data } = useCreditBalance();

  if (!data) return null;

  const { balance, allowance, purchased, spent7d } = data;
  // Runway, when there is any spending to extrapolate from.
  const forecast = creditForecast({ balance, spent7d });
  const out = balance <= 0;
  const low = !out && allowance > 0 && balance < allowance * 0.2;
  const fmt = (n: number) =>
    n.toLocaleString(i18n.language?.startsWith('de') ? 'de-DE' : 'en-US');

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          // Marker for the mobile header's `:has()` rule — its presence is what
          // tells the row it is carrying a signed-in user's controls and must
          // give up the wordmark. See header-mobile.tsx row 1.
          data-credits-pill=""
          aria-label={t('credits.pill.ariaLabel', {
            n: balance,
            defaultValue: 'Credit balance: {{n}}',
          })}
          className={cn(
            'inline-flex h-9 cursor-pointer items-center gap-1.5 rounded-full border transition-colors',
            compact ? 'px-2.5' : 'px-3',
            out
              ? 'border-destructive/40 bg-destructive/10 text-destructive'
              : low
                ? 'border-amber-500/40 bg-amber-500/10 text-amber-600 dark:text-amber-400'
                : 'border-[var(--fd-line-2)] bg-transparent text-[var(--fd-ink-2)] hover:bg-[var(--fd-bg-2)]',
          )}
        >
          <Wallet className="size-4 shrink-0" aria-hidden />
          <span className="fd-mono text-[11px] font-medium tracking-[0.04em]">
            {fmt(balance)}
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-64 p-4">
        <p className="mb-3 text-sm font-semibold">
          {t('credits.pill.title', 'Your credits')}
        </p>
        <dl className="mb-4 space-y-1.5 text-sm">
          <div className="flex items-center justify-between gap-2">
            <dt className="text-muted-foreground">
              {t('credits.pill.remaining', 'Remaining')}
            </dt>
            <dd
              className={cn(
                'font-medium tabular-nums',
                out && 'text-destructive',
                low && 'text-amber-600 dark:text-amber-400',
              )}
            >
              {fmt(balance)}
            </dd>
          </div>
          {allowance > 0 && (
            <div className="flex items-center justify-between gap-2">
              <dt className="text-muted-foreground">
                {t('credits.pill.allowance', 'Period allowance')}
              </dt>
              <dd className="font-medium tabular-nums">{fmt(allowance)}</dd>
            </div>
          )}
          {purchased > 0 && (
            <div className="flex items-center justify-between gap-2">
              <dt className="text-muted-foreground">
                {t('credits.pill.purchased', 'Purchased (never expire)')}
              </dt>
              <dd className="font-medium tabular-nums">{fmt(purchased)}</dd>
            </div>
          )}
        </dl>
        {forecast && (
          <p
            className={cn(
              'mb-3 text-xs',
              forecast.urgent
                ? 'text-amber-600 dark:text-amber-400'
                : 'text-muted-foreground',
            )}
          >
            {t('credits.pill.forecast', {
              days: forecast.daysLeft,
              defaultValue: 'About {{days}} days left at your current rate.',
            })}
          </p>
        )}
        <div className="flex items-center gap-2">
          <Button asChild size="sm" className="flex-1">
            <LocaleLink href="/plans">
              {t('credits.pill.topUp', 'Top up')}
            </LocaleLink>
          </Button>
          <Button asChild size="sm" variant="outline" className="flex-1">
            <LocaleLink href="/account/usage">
              {t('credits.pill.viewUsage', 'Usage')}
            </LocaleLink>
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
